import { useEffect, useState } from 'react';
import { CostConcept } from '../types';
import { formatCOP, formatUSD, formatPercentage } from '../utils/formatters';
import { getCustomRates, CustomRates, SalaryTypeKey } from '../services/customRatesService';
import { Download, Trash2, FolderPlus } from 'lucide-react';
import styles from './ConceptTable.module.css';

interface Section {
  title: string;
  concepts: CostConcept[];
}

interface UnifiedTableProps {
  sections: Section[];
  currentSalaryType?: SalaryTypeKey;
  onExport?: () => void;
  onClear?: () => void;
  onSaveToProject?: () => void;
}

export function UnifiedCTCTable({ sections, currentSalaryType = 'ordinario_inf', onExport, onClear, onSaveToProject }: UnifiedTableProps) {
  const [customRates, setCustomRates] = useState<CustomRates>({});

  useEffect(() => {
    const rates = getCustomRates(currentSalaryType);
    setCustomRates(rates);
  }, [currentSalaryType]);

  const getDisplayRate = (concept: CostConcept): number => {
    // Solo mostrar customRate si el concepto original es un porcentaje
    // Para valores fijos (COP o USD), siempre retornar 0
    if (concept.percentage === 0 && concept.name !== 'Salary') {
      return 0; // Valores fijos no tienen porcentaje
    }
    if (customRates[concept.name] !== undefined) {
      return customRates[concept.name];
    }
    return concept.percentage;
  };

  return (
    <div className={styles.container}>
      <div className={styles.tableHeader}>
        <div className={styles.editActions}>
          {onExport && (
            <button className={styles.exportButton} onClick={onExport} title="Exportar resultados">
              <Download size={16} />
              <span>Exportar</span>
            </button>
          )}
          {onSaveToProject && (
            <button className={styles.saveProjectButton} onClick={onSaveToProject} title="Guardar en proyecto">
              <FolderPlus size={16} />
              <span>Guardar</span>
            </button>
          )}
          {onClear && (
            <button className={styles.clearButton} onClick={onClear} title="Limpiar">
              <Trash2 size={16} />
              <span>Limpiar</span>
            </button>
          )}
        </div>
      </div>
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>CONCEPTO</th>
              <th>REF / %</th>
              <th>COP (MENSUAL)</th>
              <th>USD (MENSUAL)</th>
            </tr>
          </thead>
          <tbody>
            {sections.map((section, sectionIndex) => (
              section.concepts.length > 0 && (
                <SectionRows 
                  key={sectionIndex} 
                  title={section.title} 
                  concepts={section.concepts}
                  isFirst={sectionIndex === 0}
                  customRates={customRates}
                  getDisplayRate={getDisplayRate}
                />
              )
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface SectionRowsProps {
  title: string;
  concepts: CostConcept[];
  isFirst: boolean;
  customRates: CustomRates;
  getDisplayRate: (concept: CostConcept) => number;
}

function SectionRows({ title, concepts, isFirst, customRates, getDisplayRate }: SectionRowsProps) {
  const isSubtotalSection = concepts.every(c => c.isSubtotal);
  
  return (
    <>
      {/* Section header row - only if title exists and not a subtotal section */}
      {title && !isSubtotalSection && (
        <tr className={`${styles.sectionHeader} ${isFirst ? '' : styles.sectionHeaderSpaced}`}>
          <td colSpan={4}>{title}</td>
        </tr>
      )}
      
      {/* Concept rows */}
      {concepts.map((concept, index) => {
        const displayRate = getDisplayRate(concept);
        const hasCustomRate = customRates[concept.name] !== undefined;
        
        return (
          <tr 
            key={index} 
            className={`
              ${concept.applies ? '' : styles.notApplies}
              ${concept.isSubtotal ? styles.subtotalRow : ''}
            `}
          >
            <td className={styles.conceptName}>
              {concept.isSubtotal ? <strong>{concept.name}</strong> : concept.name}
            </td>
            <td className={styles.percentage}>
              <span className={hasCustomRate ? styles.customRate : ''}>
                {displayRate > 0 ? formatPercentage(displayRate) : '-'}
              </span>
            </td>
            <td className={styles.amount}>
              {concept.isSubtotal ? <strong>{formatCOP(concept.amountCOP)}</strong> : formatCOP(concept.amountCOP)}
            </td>
            <td className={styles.amount}>
              {concept.isSubtotal ? <strong>{formatUSD(concept.amountUSD)}</strong> : formatUSD(concept.amountUSD)}
            </td>
          </tr>
        );
      })}
    </>
  );
}

// Keep old component for backwards compatibility if needed
interface ConceptTableProps {
  title: string;
  concepts: CostConcept[];
  showSubtotal?: boolean;
}

export function ConceptTable({ title, concepts, showSubtotal = false }: ConceptTableProps) {
  if (concepts.length === 0) return null;
  
  return (
    <UnifiedCTCTable sections={[{ title, concepts }]} />
  );
}
